/**
 * @file: DeliveryCheckManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { CommonTaskStatus, DeliveryCheckSceneType } from '@euler/model/enum';
import {
  CachedTaskDetail,
  DeliveryCheck,
  TaskDetailVersions,
} from '@euler/model/task-detail';
import { ITaskManager, TaskFileUploadRequestContext } from '../types';
import { DeliveryCheckManagerBase } from './DeliveryCheckManagerBase';

export class DeliveryCheckManager extends DeliveryCheckManagerBase<DeliveryCheck> {
  constructor(taskManager: ITaskManager) {
    super(taskManager, DeliveryCheckSceneType.CompletionCheck);
  }

  async save(options?: { saveOnly?: boolean }) {
    const payload = this.getCommitPayload(options?.saveOnly);

    payload.saveOnly = options?.saveOnly;

    const resp = await this.taskService.saveDeliveryChecks(
      this.taskNo,
      payload,
    );

    this.handleVersionedResponse(resp, (_, { deliveryCheck, response }) => {
      Object.assign(deliveryCheck, response);
    });

    await this.discardCache();

    this.withChangeObserverTemporarilyDisabled(() => {
      this.resetWithDeliveryCheck(resp.response);
    });
  }

  async finish(options?: { remark?: string; signatureImgUrl?: string }) {
    const resp = await this.taskService.finishDeliveryChecks(this.taskNo, {
      sceneType: DeliveryCheckSceneType.CompletionCheck,
      ...options,
    });
    this.handleVersionedResponse(resp, (_, { deliveryCheck }) => {
      deliveryCheck.status = CommonTaskStatus.Finished;
      deliveryCheck.finishedAt = new Date();
      deliveryCheck.finishedBy = this.currentUser.name;
    });
    await this.discardCache();
  }

  async requestAmend() {
    const resp = await this.taskService.requestUpdateDeliveryChecks(
      this.taskNo,
      DeliveryCheckSceneType.CompletionCheck,
    );
    this.handleVersionedResponse(resp, (_, { deliveryCheck }) => {
      deliveryCheck.status = CommonTaskStatus.InProgress;
    });
  }

  protected getDeliveryCheck(detail: CachedTaskDetail): DeliveryCheck {
    return detail.deliveryCheck;
  }

  protected getDeliveryCheckVersion(versions: TaskDetailVersions): number {
    return versions.deliveryCheck;
  }

  protected getUploadEventContextType(): Extract<
    TaskFileUploadRequestContext['type'],
    'delivery-check' | 'sampling-check'
  > {
    return 'delivery-check';
  }
}
